/* eslint-disable unicorn/prevent-abbreviations */
import { RequirementWorkflowType } from "@clipboard-health/contract-worker-app-bff";
import {
  PageHeaderWrapper,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DocumentStatus } from "@src/appV2/Accounts/Documents/types";
import { FullScreenDialog } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { PandaDocFailureBottomSheet } from "@src/appV2/redesign/Documents/Details/Workflows/PandaDoc/PandaDocFailureBottomSheet";
import { type ESignWorkflowSessionAttributes } from "@src/appV2/redesign/Documents/Details/Workflows/PandaDoc/types";
import {
  SHIFT_DISCOVERY_SHIFT_DOCUMENT_SUBMITTED_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "@src/appV2/redesign/ShiftDiscovery/paths";
import { type HelloSignModalRoutePathParams } from "@src/appV2/redesign/ShiftDiscovery/types";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { PageHeaderTopRow } from "../../../../components/PageHeaderTopRow";
import { PandaDocSigningContent } from "../../../../Documents/Details/Workflows/PandaDoc/PandaDocSigningContent";
import { QuizNotPassedBottomSheet } from "../../../../Documents/Details/Workflows/PandaDoc/QuizNotPassedBottomSheet/QuizNotPassedBottomSheet";
import { usePandaDocSigningSession } from "../../../../Documents/Details/Workflows/PandaDoc/usePandaDocSigningSession";
import { DocumentSubmissionInProgressBottomSheet } from "../../../../Documents/Submission/InProgressBottomSheet";

interface InstantReviewPandaDocFormBottomSheetProps {
  modalState: UseModalState;
}

const signType = "shiftBookingFlow";

export function InstantReviewPandaDocFormBottomSheet(
  props: InstantReviewPandaDocFormBottomSheetProps
) {
  const { modalState } = props;
  const submittingDocumentModalState = useModalState();
  const errorModalState = useModalState();
  const quizNotPassedModalState = useModalState();
  const [quizResultData, setQuizResultData] =
    useState<NonNullable<ESignWorkflowSessionAttributes["quizResult"]>>();
  const { navigateToModal } = useShiftModalsDataContext();
  const { shiftId, requirementId } = useParams<HelloSignModalRoutePathParams>();
  const {
    signableESignWorkflowSession,
    isLoadingPandaDoc,
    startSigning,
    handleDocumentLoaded,
    completeSigningAndGetDocumentDetails,
  } = usePandaDocSigningSession({
    requirementId,
    signType,
    onSessionLoadError: useCallback(
      (error: unknown) => {
        logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_FAILED, {
          shiftId: shiftId ?? "",
          requirementId,
          requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
        });
        logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
          error,
          metadata: { requirementId, shiftId },
        });
        errorModalState.openModal();
      },
      [errorModalState, shiftId, requirementId]
    ),
  });

  const onPandaDocCompleted = useCallback(async () => {
    submittingDocumentModalState.openModal();
    try {
      const { documentDetails, quizResult } = await completeSigningAndGetDocumentDetails();

      const { status: documentStatus, notification } = documentDetails ?? {};

      logEvent(APP_V2_USER_EVENTS.PANDADOC_SDK_DOCUMENT_SIGNED, {
        requirementId,
        signType,
      });

      logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_SUCCESSFUL, {
        shiftId: shiftId ?? "",
        requirementId,
        quizResultOutcome: quizResult?.overallResult,
        documentStatus: documentStatus ?? DocumentStatus.PENDING,
        requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
      });

      if (documentStatus === DocumentStatus.REJECTED && isDefined(quizResult)) {
        submittingDocumentModalState.closeModal();
        setQuizResultData(quizResult);
        quizNotPassedModalState.openModal();
        return;
      }

      submittingDocumentModalState.closeModal();
      const rejectionDescription = notification?.note ?? notification?.reason;
      navigateToModal(SHIFT_DISCOVERY_SHIFT_DOCUMENT_SUBMITTED_MODAL_PATH, {
        pathParams: {
          shiftId,
          requirementId,
          documentStatus: documentStatus ?? DocumentStatus.PENDING,
        },
        ...(rejectionDescription && documentStatus === DocumentStatus.REJECTED
          ? { state: { description: rejectionDescription } }
          : {}),
        forceReplacePath: true,
      });
    } catch (error: unknown) {
      submittingDocumentModalState.closeModal();
      errorModalState.openModal();
      logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
        error,
        metadata: { requirementId, shiftId },
      });
      logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_FAILED, {
        shiftId: shiftId ?? "",
        requirementId,
        requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
      });
    }
  }, [
    completeSigningAndGetDocumentDetails,
    navigateToModal,
    submittingDocumentModalState,
    errorModalState,
    quizNotPassedModalState,
    shiftId,
    requirementId,
  ]);

  const handleRetakeQuiz = useCallback(() => {
    quizNotPassedModalState.closeModal();
    setQuizResultData(undefined);
    startSigning();
  }, [quizNotPassedModalState, startSigning]);

  const handleCloseQuizNotPassedModal = useCallback(() => {
    quizNotPassedModalState.closeModal();
    setQuizResultData(undefined);
    navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
      pathParams: { shiftId },
      forceReplacePath: true,
    });
  }, [quizNotPassedModalState, navigateToModal, shiftId]);

  useEffect(() => {
    if (requirementId) {
      startSigning();
    }
  }, [requirementId, startSigning, errorModalState]);

  return (
    <>
      <FullScreenDialog skipTopSafeAreaPadding variant="tertiary" modalState={modalState}>
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow
            title="Sign Document"
            onClickBack={() => {
              logEvent(APP_V2_USER_EVENTS.PANDADOC_SIGNING_CLOSED, {
                requirementId,
                signType,
              });
              modalState.closeModal();
            }}
          />
        </PageHeaderWrapper>
        <PandaDocSigningContent
          isLoading={isLoadingPandaDoc}
          pandaDocSessionId={signableESignWorkflowSession?.attributes.esignProviderData?.sessionId}
          onDocumentLoaded={handleDocumentLoaded}
          onDocumentRendered={() => {
            logEvent(APP_V2_APP_EVENTS.PANDADOC_SDK_DOCUMENT_RENDERED, {
              pandaDocSessionId:
                signableESignWorkflowSession?.attributes.esignProviderData?.sessionId,
              signType,
            });
          }}
          onError={() => {
            logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_FAILED, {
              shiftId: shiftId ?? "",
              requirementId,
              requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
            });
            logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
              error: new Error("PandaDoc Embedded Signing SDK Error"),
              metadata: { requirementId, shiftId },
            });
            errorModalState.openModal();
          }}
          onComplete={onPandaDocCompleted}
        />

        <DocumentSubmissionInProgressBottomSheet modalState={submittingDocumentModalState} />
      </FullScreenDialog>

      <PandaDocFailureBottomSheet
        modalState={errorModalState}
        onClose={() => {
          navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
            pathParams: { shiftId },
            forceReplacePath: true,
          });
        }}
      />
      {isDefined(quizResultData) && (
        <QuizNotPassedBottomSheet
          modalState={quizNotPassedModalState}
          quizResult={quizResultData}
          onClose={handleCloseQuizNotPassedModal}
          onRetakeQuiz={handleRetakeQuiz}
        />
      )}
    </>
  );
}
/* eslint-enable unicorn/prevent-abbreviations */
